import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Globe, Search, BarChart3, Clock, ShoppingBag, ArrowRight,
  ExternalLink, FileText, TrendingUp,
  ArrowUpRight, RefreshCw, Loader2, Eye, ChevronLeft,
  ChevronRight, Upload, ImageIcon, FolderOpen, Check, Info, CreditCard,
  Sparkles, Send, LogIn, MessageSquare, Percent
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import SimpleEditor from "@/components/SimpleEditor";
import AuthForm from "@/components/AuthForm";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prosty edytor z podgladem HTML

export default function PublicShop({ onLogin }) {
  const { language, t } = useLanguage();
  const auth = useAuth();
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteDetail, setSiteDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [updatingScreenshot, setUpdatingScreenshot] = useState(false);
  
  // Prowizja portalu
  const [commissionPercent, setCommissionPercent] = useState(5.0);

  // Order wizard
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderStep, setOrderStep] = useState(0); // 0=offer, 1=content, 2=summary
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [orderForm, setOrderForm] = useState({
    title: "", content: "", meta_title: "", meta_description: "",
    image_file: null, image_preview: null, image_from_library: false,
    guest_email: "", requirements: "",
  });
  const [imageResizeNeeded, setImageResizeNeeded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaFilter, setMediaFilter] = useState("all");
  const [mediaCategories, setMediaCategories] = useState([]);

  // Negocjacja
  const [showNegotiation, setShowNegotiation] = useState(false);
  const [negotiationOffer, setNegotiationOffer] = useState(null);
  const [negotiationData, setNegotiationData] = useState({
    proposed_price: "",
    message: ""
  });
  const [negotiationLoading, setNegotiationLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCat !== "all") params.set("category", filterCat);
      if (searchQ.trim()) params.set("q", searchQ.trim());
      const [searchRes, catsRes, commissionRes] = await Promise.all([
        axios.get(`${API}/shop/search?${params.toString()}`),
        axios.get(`${API}/shop/categories`),
        axios.get(`${API}/public/commission`).catch(() => ({ data: { commission_percent: 5.0 } })),
      ]);
      setResults(searchRes.data);
      setCategories(catsRes.data.categories || []);
      setCommissionPercent(commissionRes.data.commission_percent || 5.0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCat, searchQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (site) => {
    setSelectedSite(site);
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/shop/site/${site.site_id}`);
      setSiteDetail(res.data);
    } catch (err) { console.error(err); }
    finally { setLoadingDetail(false); }
  };

  const updateScreenshot = async (siteId) => {
    setUpdatingScreenshot(true);
    try {
      const res = await axios.post(`${API}/shop/site/${siteId}/screenshot`);
      setSiteDetail(prev => prev ? { ...prev, screenshot_url: res.data.screenshot_url } : prev);
    } catch (err) { console.error(err); }
    finally { setUpdatingScreenshot(false); }
  };

  const getLowestPrice = (site) => {
    return site.lowest_price || null;
  };

  // === ORDER FUNCTIONS ===
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null);
  
  const startOrder = (offer) => {
    // Sprawdz czy zalogowany - jesli nie, pokaz modal logowania
    if (!auth.isLoggedIn) {
      setPendingOffer(offer);
      setShowAuthDialog(true);
      return;
    }
    
    setSelectedOffer(offer);
    setOrderStep(1); // zawsze krok 1 - ale formularz rozny
    setOrderForm({ title: "", content: "", meta_title: "", meta_description: "",
      image_file: null, image_preview: null, image_from_library: false,
      image_alt: "", guest_email: "", requirements: "",
      target_phrase: "", target_url: "", article_brief: "" });
    setImageResizeNeeded(false);
    setOrderDialog(true);
  };
  
  // Po zalogowaniu kontynuuj zamowienie
  const handleAuthSuccess = () => {
    setShowAuthDialog(false);
    if (pendingOffer) {
      setSelectedOffer(pendingOffer);
      setOrderStep(1);
      setOrderForm({ title: "", content: "", meta_title: "", meta_description: "",
        image_file: null, image_preview: null, image_from_library: false,
        image_alt: "", guest_email: "", requirements: "",
        target_phrase: "", target_url: "", article_brief: "" });
      setImageResizeNeeded(false);
      setOrderDialog(true);
      setPendingOffer(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const needsResize = img.width !== 800 || img.height !== 600;
        setImageResizeNeeded(needsResize);
        setOrderForm(f => ({ ...f, image_file: ev.target.result, image_preview: ev.target.result, image_from_library: false }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const loadMedia = async () => {
    try {
      const [mediaRes, catsRes] = await Promise.all([
        axios.get(`${API}/media`),
        axios.get(`${API}/media/categories`),
      ]);
      setMediaItems(mediaRes.data);
      setMediaCategories(catsRes.data.categories || []);
    } catch {}
  };

  const selectFromLibrary = (img) => {
    setOrderForm(f => ({ ...f, image_file: img.url, image_preview: img.url, image_from_library: true }));
    setImageResizeNeeded(false);
    setMediaPickerOpen(false);
  };

  // === NEGOCJACJA ===
  const openNegotiation = (offer) => {
    if (!auth.isLoggedIn) {
      toast.error("Zaloguj sie aby negocjowac cene");
      setShowAuthDialog(true);
      return;
    }
    setNegotiationOffer(offer);
    setNegotiationData({
      proposed_price: "",
      message: ""
    });
    setShowNegotiation(true);
  };

  const handleNegotiation = async () => {
    if (!auth.isLoggedIn) {
      toast.error("Musisz byc zalogowany aby negocjowac cene");
      setShowAuthDialog(true);
      return;
    }
    
    if (!negotiationData.proposed_price || parseFloat(negotiationData.proposed_price) <= 0) {
      toast.error("Podaj proponowana cene");
      return;
    }
    
    setNegotiationLoading(true);
    try {
      await axios.post(`${API}/negotiations`, {
        site_id: siteDetail?.site?.id,
        offer_name: negotiationOffer.name,
        current_price: negotiationOffer.price,
        proposed_price: parseFloat(negotiationData.proposed_price),
        message: negotiationData.message
      });
      
      toast.success("Propozycja wyslana! Admin skontaktuje sie z Toba.");
      setShowNegotiation(false);
      setNegotiationOffer(null);
      setNegotiationData({ proposed_price: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad wysylania propozycji");
    } finally {
      setNegotiationLoading(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!auth.isLoggedIn && !orderForm.guest_email.trim()) {
      toast.error("Podaj email lub zaloguj sie");
      return;
    }
    setSubmitting(true);
    try {
      const basePrice = selectedOffer?.price || 0;
      const formatCost = aiFormatApplied ? 3 : 0;
      const resizeCost = imageResizeNeeded && !orderForm.image_from_library ? 1 : 0;
      const supportCost = supportArticles ? Math.round(basePrice * 0.5) * supportCount : 0;
      const price = basePrice + formatCost + resizeCost + supportCost;
      await axios.post(`${API}/shop/order`, {
        order_type: selectedOffer?.type || "article",
        site_id: siteDetail?.site?.id,
        title: orderForm.title,
        content: orderForm.content,
        meta_title: orderForm.meta_title,
        meta_description: orderForm.meta_description,
        image_data: orderForm.image_file,
        requirements: orderForm.requirements,
        guest_email: orderForm.guest_email,
        price: price,
        offer_name: selectedOffer?.name,
        image_resize: imageResizeNeeded && !orderForm.image_from_library,
      });
      toast.success("Zamowienie zlozone!");
      setOrderDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Blad zamowienia"); }
    finally { setSubmitting(false); }
  };

  // AI Format za 3 PLN
  const [aiFormatting, setAiFormatting] = useState(false);
  const [aiFormatApplied, setAiFormatApplied] = useState(false);
  const [supportArticles, setSupportArticles] = useState(false);
  const [supportCount, setSupportCount] = useState(3);

  const handleAiFormat = async () => {
    if (!orderForm.content.trim()) { toast.error("Wklej najpierw tresc artykulu"); return; }
    setAiFormatting(true);
    try {
      const res = await axios.post(`${API}/shop/ai-format`, {
        content: orderForm.content, title: orderForm.title
      });
      setOrderForm(f => ({
        ...f,
        content: res.data.formatted_content,
        meta_title: res.data.meta_title || f.meta_title,
        meta_description: res.data.meta_description || f.meta_description,
        image_alt: res.data.image_alt || f.image_alt,
      }));
      setAiFormatApplied(true);
      toast.success("Tresc sformatowana przez AI! Meta tagi i alt uzupelnione.");
    } catch (err) { toast.error("Blad formatowania AI"); }
    finally { setAiFormatting(false); }
  };

  // Build offers ONLY from what admin/publisher assigned to this site
  const getAllOffers = () => {
    if (!siteDetail) return [];
    const offers = [];
    // TYLKO z cennika przypisanego do strony (pricing)
    if (siteDetail.pricing_items?.length > 0) {
      for (const item of siteDetail.pricing_items) {
        const nameLower = (item.name || "").toLowerCase();
        const needsContent = nameLower.includes("klient") || nameLower.includes("dostarcza");
        offers.push({
          name: item.name, price: item.price_pln, type: item.category || "article",
          needs_content: needsContent, category: item.category || "other",
          tags: item.tags,
        });
      }
    }
    // Jesli brak cennika AI - uzyj podstawowej oferty wydawcy
    if (offers.length === 0 && siteDetail.offer) {
      const o = siteDetail.offer;
      if (o.publication_price) offers.push({ name: "Publikacja artykulu (klient dostarcza)", price: o.publication_price, type: "article", needs_content: true });
      if (o.writing_price) offers.push({ name: "Napisanie + publikacja artykulu", price: o.writing_price, type: "article_write", needs_content: false });
      if (o.link_price) offers.push({ name: "Link / Wpis", price: o.link_price, type: "link", needs_content: false });
    }
    return offers;
  };

  const allOffers = siteDetail ? getAllOffers() : [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <Header onLogin={onLogin} />

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{t("publicShop.title")}</h1>
            <p className="text-sm text-slate-500 mb-4">{t("publicShop.subtitle")}</p>
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  placeholder={t("publicShop.searchPlaceholder")} className="pl-10 h-10 rounded-xl bg-white" />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="h-10 w-[160px] rounded-xl bg-white"><SelectValue placeholder={t("publicShop.allCategories")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("publicShop.allCategories")}</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge className="bg-white text-slate-600 border border-slate-200 text-xs">{results.length} {t("publicShop.results")}</Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista portali */}
            <div className={selectedSite ? "lg:col-span-1" : "lg:col-span-3"}>
              {results.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                  <Globe className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>{t("publicShop.noResults")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map((site) => {
                    const lowest = getLowestPrice(site);
                    return (
                      <button key={site.offer_id} onClick={() => openDetail(site)}
                        className={`w-full text-left rounded-xl border bg-white p-4 transition-all hover:shadow-md ${
                          selectedSite?.site_id === site.site_id ? "border-blue-300 shadow-md ring-1 ring-blue-100" : "border-slate-100"
                        }`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <Globe className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-sm font-semibold text-slate-900 truncate">{site.site_name}</span>
                              {site.site_category && <Badge variant="outline" className="text-[8px] shrink-0">{site.site_category}</Badge>}
                            </div>
                            <p className="text-[11px] text-slate-400 truncate">{site.site_url}</p>
                          </div>
                          {lowest && (
                            <div className="text-right shrink-0">
                              <p className="text-[10px] text-slate-400">{t("publicShop.from")}</p>
                              <p className="text-base font-bold text-blue-600">{lowest} <span className="text-[10px] font-normal">PLN</span></p>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* SZCZEGOLY */}
            {selectedSite && siteDetail && !loadingDetail && (
              <div className="lg:col-span-2 space-y-4">
                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{siteDetail.site.name}</h2>
                      <a href={siteDetail.site.url} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        {siteDetail.site.url} <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1 text-xs" asChild>
                      <a href={siteDetail.site.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /> {language === 'pl' ? 'Odwiedź' : 'Visit'}</a>
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {siteDetail.site.category && <Badge variant="outline">{siteDetail.site.category}</Badge>}
                    {siteDetail.site.region && <Badge className="bg-slate-100 text-slate-600 border-0">{siteDetail.site.region}</Badge>}
                  </div>
                  {siteDetail.site.ai_analysis?.target_audience && (
                    <p className="text-sm text-slate-500 leading-relaxed">{siteDetail.site.ai_analysis.target_audience}</p>
                  )}
                </div>

                {/* Screenshot */}
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                    <span className="text-xs font-medium text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Podglad strony</span>
                    <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={() => updateScreenshot(siteDetail.site.id)} disabled={updatingScreenshot}>
                      {updatingScreenshot ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {language === 'pl' ? 'Aktualizuj' : 'Refresh'}
                    </Button>
                  </div>
                  <div className="h-[300px] bg-slate-100 overflow-hidden">
                    {siteDetail.screenshot_url ? (
                      <img src={siteDetail.screenshot_url} alt="" className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-slate-400 text-xs">{language === 'pl' ? 'Kliknij Aktualizuj' : 'Click Refresh'}</div>
                    )}
                  </div>
                </div>

                {/* Statystyki */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Opublikowano", value: siteDetail.stats.total_published, icon: FileText },
                    { label: "Ostatnie 7 dni", value: siteDetail.stats.last_7_days, icon: TrendingUp },
                    { label: "Ostatnie 24h", value: siteDetail.stats.last_24h, icon: Clock },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-xl border p-3 text-center">
                      <s.icon className="w-4 h-4 text-slate-400 mx-auto mb-1" strokeWidth={1.5} />
                      <p className="text-xl font-bold text-slate-900">{s.value}</p>
                      <p className="text-[9px] text-slate-500">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* === ANALIZA AI === */}
                {siteDetail.site.ai_analysis && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-5">
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-blue-500" /> Analiza AI portalu
                    </h3>
                    
                    {/* Kategorie i Grupa docelowa */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Kategorie */}
                      {siteDetail.site.ai_analysis.categories && siteDetail.site.ai_analysis.categories.length > 0 && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Kategorie</p>
                          <div className="flex flex-wrap gap-1.5">
                            {siteDetail.site.ai_analysis.categories.map((cat, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-white">{cat}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Grupa docelowa */}
                      {siteDetail.site.ai_analysis.target_audience && (
                        <div className="bg-blue-50 rounded-xl p-4">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400 mb-2">Grupa docelowa</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{siteDetail.site.ai_analysis.target_audience}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Mocne strony */}
                    {siteDetail.site.ai_analysis.strengths && siteDetail.site.ai_analysis.strengths.length > 0 && (
                      <div className="bg-emerald-50/50 rounded-xl p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500 mb-3">Mocne strony</p>
                        <div className="space-y-2">
                          {siteDetail.site.ai_analysis.strengths.map((str, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <div className="w-1 h-full min-h-[20px] bg-blue-500 rounded-full shrink-0 mt-0.5" />
                              <p className="text-sm text-slate-700">{str}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Slabe strony */}
                    {siteDetail.site.ai_analysis.weaknesses && siteDetail.site.ai_analysis.weaknesses.length > 0 && (
                      <div className="bg-red-50/50 rounded-xl p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400 mb-3">Slabe strony / Do poprawy</p>
                        <div className="space-y-2">
                          {siteDetail.site.ai_analysis.weaknesses.map((weak, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <div className="w-1 h-full min-h-[20px] bg-red-400 rounded-full shrink-0 mt-0.5" />
                              <p className="text-sm text-slate-700">{weak}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Top frazy Google */}
                    {siteDetail.site.ai_analysis.top_keywords && siteDetail.site.ai_analysis.top_keywords.length > 0 && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Top frazy wyszukiwania Google</p>
                        <div className="space-y-1.5">
                          {siteDetail.site.ai_analysis.top_keywords.slice(0, 10).map((kw, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-xs font-bold text-slate-400 w-5 text-right">{i + 1}.</span>
                              <Badge variant="outline" className="text-xs font-normal">{kw}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === WSZYSTKIE OFERTY === */}
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <h3 className="text-base font-bold text-slate-900 mb-1">Dostepne oferty</h3>
                  <p className="text-xs text-slate-400 mb-4">{allOffers.length} {language === 'pl' ? 'ofert do wyboru - kliknij "Zamów" przy wybranej' : 'offers available - click "Order" to select'}</p>
                  <div className="space-y-2">
                    {allOffers.map((offer, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-blue-300 hover:shadow-md transition-all group bg-white">
                        <div className="w-2 h-12 rounded-full bg-blue-500 shrink-0 group-hover:bg-blue-600 transition-colors" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{offer.name}</p>
                          {offer.tags && (
                            <div className="flex gap-1 mt-1">
                              {(Array.isArray(offer.tags) ? offer.tags : []).slice(0, 3).map((tag, ti) => (
                                <Badge key={ti} variant="outline" className="text-[8px]">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">{offer.price}</p>
                            <p className="text-[10px] text-slate-400">PLN netto</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="lg" 
                            className="h-11 px-4 gap-2 rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => openNegotiation(offer)}
                            data-testid={`negotiate-btn-${i}`}
                          >
                            <MessageSquare className="w-4 h-4" /> {t("publicShop.negotiate")}
                          </Button>
                          <Button size="lg" className="h-11 px-6 gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-base font-semibold shadow-md"
                            onClick={() => startOrder(offer)}>
                            <ShoppingBag className="w-4 h-4" /> {t("publicShop.order")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {loadingDetail && selectedSite && (
              <div className="lg:col-span-2 flex items-center justify-center h-64 bg-white rounded-2xl border">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* === ORDER WIZARD === */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="font-heading text-lg">{selectedOffer?.name}</DialogTitle>
                <p className="text-sm text-blue-600 font-bold">{selectedOffer?.price} PLN</p>
              </div>
            </div>
          </DialogHeader>

          {/* Krok 1: Formularz - rozny w zaleznosci od oferty */}
          {orderStep === 1 && (
            <div className="space-y-4 py-3">

              {/* === KLIENT DOSTARCZA TRESC === */}
              {selectedOffer?.needs_content ? (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Tytul artykulu *</Label>
                    <Input value={orderForm.title} onChange={(e) => setOrderForm({...orderForm, title: e.target.value})}
                      placeholder="Tytul artykulu..." className="h-11 text-base font-medium" />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium">Tresc artykulu *</Label>
                      <span className="text-[9px] text-slate-400">Formatuj sam lub uzyj AI ponizej</span>
                    </div>
                    <SimpleEditor value={orderForm.content}
                      onChange={(html) => setOrderForm(f => ({...f, content: html}))}
                      placeholder="Wklej lub napisz tresc artykulu..." />
                  </div>

                  {/* AI Format za 3 PLN */}
                  {orderForm.content.length > 50 && !aiFormatApplied && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900">Uformatuj tresc przez AI</p>
                          <p className="text-xs text-slate-500 mt-0.5">AI podzieli tekst na czytelne akapity, doda naglowki H2/H3, wyrózni wazne frazy, uzupelni meta tytul, meta opis i alt zdjecia.</p>
                        </div>
                        <Button onClick={handleAiFormat} disabled={aiFormatting}
                          className="shrink-0 h-10 px-5 bg-blue-600 hover:bg-blue-700 gap-2 rounded-xl text-sm">
                          {aiFormatting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          Formatuj +3 PLN
                        </Button>
                      </div>
                    </div>
                  )}
                  {aiFormatApplied && (
                    <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Tresc sformatowana przez AI</p>
                        <p className="text-[10px] text-emerald-600">Meta tytul, meta opis i alt zdjecia uzupelnione automatycznie. Koszt +3 PLN.</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Meta tytul (SEO)</Label>
                      <Input value={orderForm.meta_title} onChange={(e) => setOrderForm({...orderForm, meta_title: e.target.value})}
                        placeholder="max 60 znakow" className="h-10" />
                      <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${orderForm.meta_title.length > 60 ? "bg-red-500" : orderForm.meta_title.length > 40 ? "bg-emerald-500" : "bg-blue-400"}`}
                          style={{width: `${Math.min(100, (orderForm.meta_title.length / 60) * 100)}%`}} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Meta opis (SEO)</Label>
                      <Input value={orderForm.meta_description} onChange={(e) => setOrderForm({...orderForm, meta_description: e.target.value})}
                        placeholder="max 160 znakow" className="h-10" />
                      <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${orderForm.meta_description.length > 160 ? "bg-red-500" : orderForm.meta_description.length > 100 ? "bg-emerald-500" : "bg-blue-400"}`}
                          style={{width: `${Math.min(100, (orderForm.meta_description.length / 160) * 100)}%`}} />
                      </div>
                    </div>
                  </div>

                  {/* Zdjecie */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Zdjecie do artykulu</Label>
                    <div className="flex items-start gap-3">
                      <div className="w-20 rounded-lg border bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden" style={{height: 60}}>
                        {orderForm.image_preview ? (
                          <img src={orderForm.image_preview} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <div className="flex gap-1.5">
                          <label className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs text-slate-600 hover:bg-slate-50 cursor-pointer">
                            <Upload className="w-3 h-3" /> Z komputera
                            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                          </label>
                          <button onClick={() => { loadMedia(); setMediaPickerOpen(true); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs text-slate-600 hover:bg-slate-50">
                            <FolderOpen className="w-3 h-3" /> Z bazy zdjec
                          </button>
                        </div>
                        {imageResizeNeeded && !orderForm.image_from_library && (
                          <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium">Zdjecie ma inny rozmiar niz 800x600px</p>
                              <p className="text-amber-600">System dostosuje rozmiar. Koszt: <strong>+1 PLN</strong></p>
                              <p className="text-amber-500 text-[9px]">Mozesz wgrac zdjecie 800x600px aby uniknac dodatkowej oplaty</p>
                            </div>
                          </div>
                        )}
                        {orderForm.image_from_library && (
                          <p className="text-[10px] text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" /> Zdjecie z bazy 800x600px - bez dodatkowych kosztow</p>
                        )}
                      </div>
                    </div>
                    {orderForm.image_preview && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Alt zdjecia (opis dla wyszukiwarek) *</Label>
                        <Input value={orderForm.image_alt} onChange={(e) => setOrderForm({...orderForm, image_alt: e.target.value})}
                          placeholder="Np. Mezczyzna skladajacy meble w salonie" />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* === WYDAWCA PISZE TRESC === */
                <>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-xs font-medium text-blue-800">Wydawca napisze artykul za Ciebie</p>
                    <p className="text-[10px] text-blue-600 mt-0.5">Opisz ponizej czego oczekujesz</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">O czym ma byc artykul? *</Label>
                    <Textarea value={orderForm.article_brief} onChange={(e) => setOrderForm({...orderForm, article_brief: e.target.value})}
                      rows={3} className="resize-none text-xs"
                      placeholder="Np. Napisz poradnikowy artykul o skladaniu mebli IKEA, porady dla poczatkujacych, jakie narzedzia sa potrzebne..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Fraza kluczowa do uzycia w artykule *</Label>
                    <Input value={orderForm.target_phrase} onChange={(e) => setOrderForm({...orderForm, target_phrase: e.target.value})}
                      placeholder="Np. skladanie mebli IKEA" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">URL do podlinkowania we frazie *</Label>
                    <Input value={orderForm.target_url} onChange={(e) => setOrderForm({...orderForm, target_url: e.target.value})}
                      placeholder="https://www.twoja-strona.pl/oferta" />
                    <p className="text-[9px] text-slate-400">Ten link zostanie wstawiony we frazie kluczowej w artykule</p>
                  </div>
                </>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setOrderDialog(false)} className="flex-1">Anuluj</Button>
                <Button onClick={() => setOrderStep(2)} className="flex-1">Dalej <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>
              </div>
            </div>
          )}

          {/* Krok 2: Podsumowanie */}
          {orderStep === 2 && (
            <div className="space-y-4 py-3">
              <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Portal:</span><span className="font-medium">{siteDetail?.site?.name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Usluga:</span><span className="font-medium">{selectedOffer?.name}</span></div>
                {/* Klient dostarcza */}
                {selectedOffer?.needs_content && orderForm.title && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Tytul:</span><span className="font-medium truncate ml-2">{orderForm.title}</span></div>
                )}
                {selectedOffer?.needs_content && orderForm.image_alt && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Alt zdjecia:</span><span className="font-medium truncate ml-2">{orderForm.image_alt}</span></div>
                )}
                {/* Wydawca pisze */}
                {!selectedOffer?.needs_content && orderForm.article_brief && (
                  <div className="text-sm"><span className="text-slate-500">Opis artykulu:</span><p className="font-medium text-slate-700 mt-0.5">{orderForm.article_brief}</p></div>
                )}
                {!selectedOffer?.needs_content && orderForm.target_phrase && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Fraza:</span><span className="font-medium text-blue-600">{orderForm.target_phrase}</span></div>
                )}
                {!selectedOffer?.needs_content && orderForm.target_url && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">URL:</span><span className="font-medium text-blue-600 truncate ml-2">{orderForm.target_url}</span></div>
                )}
                {orderForm.image_preview && (
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-slate-500">Zdjecie:</span>
                    <div className="flex items-center gap-2">
                      <img src={orderForm.image_preview} alt="" className="w-10 h-8 rounded object-cover" />
                      {imageResizeNeeded && !orderForm.image_from_library && <Badge className="bg-amber-100 text-amber-700 text-[9px] border-0">Resize +1 PLN</Badge>}
                      {orderForm.image_from_library && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] border-0">800x600 OK</Badge>}
                    </div>
                  </div>
                )}
                <Separator />
                
                {/* Rozbicie kosztow z prowizja */}
                <div className="space-y-2 pt-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Cena uslugi:</span>
                    <span className="font-medium">{selectedOffer?.price || 0} PLN</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Percent className="w-3 h-3" /> Prowizja portalu ({commissionPercent}%):
                    </span>
                    <span className="font-medium text-blue-600">+{((selectedOffer?.price || 0) * commissionPercent / 100).toFixed(2)} PLN</span>
                  </div>
                  {aiFormatApplied && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Formatowanie AI:</span>
                      <span className="font-medium">+3 PLN</span>
                    </div>
                  )}
                  {imageResizeNeeded && !orderForm.image_from_library && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Resize zdjecia:</span>
                      <span className="font-medium">+1 PLN</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-base font-semibold">Lacznie do zaplaty:</span>
                    <span className="text-2xl font-bold text-blue-600" data-testid="order-total-price">
                      {(
                        (selectedOffer?.price || 0) + 
                        ((selectedOffer?.price || 0) * commissionPercent / 100) + 
                        (imageResizeNeeded && !orderForm.image_from_library ? 1 : 0) + 
                        (aiFormatApplied ? 3 : 0)
                      ).toFixed(2)} PLN
                    </span>
                  </div>
                </div>
              </div>

              {/* Email dla goscia */}
              {!auth.isLoggedIn && (
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 space-y-2">
                  <Label className="text-xs font-medium">Email kontaktowy (zamowienie bez konta)</Label>
                  <Input type="email" value={orderForm.guest_email}
                    onChange={(e) => setOrderForm({...orderForm, guest_email: e.target.value})}
                    placeholder="twoj@email.pl" />
                  <p className="text-[9px] text-blue-500">Otrzymasz potwierdzenie i status zamowienia na ten email</p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs">Dodatkowe uwagi (opcjonalnie)</Label>
                <Textarea value={orderForm.requirements} onChange={(e) => setOrderForm({...orderForm, requirements: e.target.value})}
                  rows={2} className="resize-none text-xs" placeholder="Dodatkowe wskazowki..." />
              </div>

              <div className="flex gap-3">
                {selectedOffer?.needs_content && (
                  <Button variant="outline" size="lg" onClick={() => setOrderStep(1)} className="gap-2 rounded-xl h-12"><ChevronLeft className="w-4 h-4" /> Wstecz</Button>
                )}
                <Button size="lg" className="flex-1 h-14 gap-3 rounded-xl text-lg font-bold bg-blue-600 hover:bg-blue-700 shadow-lg"
                  onClick={handleSubmitOrder} disabled={submitting}>
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                  Zloz zamowienie
                </Button>
              </div>
              <p className="text-[9px] text-slate-400 text-center">Platnosc: Stripe (placeholder). Wydawca otrzyma powiadomienie.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Media Picker z filtrem kategorii */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[75vh]">
          <DialogHeader><DialogTitle className="font-heading">Wybierz zdjecie z bazy (800x600px)</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 flex-wrap pb-2">
            <button onClick={() => setMediaFilter("all")}
              className={`px-2 py-1 rounded-md text-[10px] font-medium ${mediaFilter === "all" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
              Wszystkie ({mediaItems.length})
            </button>
            {mediaCategories.map(c => {
              const count = mediaItems.filter(m => m.category === c).length;
              if (count === 0) return null;
              return (
                <button key={c} onClick={() => setMediaFilter(c)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium ${mediaFilter === c ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                  {c} ({count})
                </button>
              );
            })}
          </div>
          <ScrollArea className="h-[45vh]">
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 p-1">
              {mediaItems.filter(m => mediaFilter === "all" || m.category === mediaFilter).map((img) => (
                <button key={img.id} onClick={() => selectFromLibrary(img)}
                  className="rounded-lg border hover:border-blue-400 hover:shadow-md transition-all overflow-hidden">
                  <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center relative">
                    {img.url ? <img src={img.url} alt={img.alt} className="w-full h-full object-cover" onError={(e) => e.target.style.display="none"} /> : null}
                    <ImageIcon className="w-6 h-6 text-slate-300 absolute" />
                  </div>
                  <div className="p-1">
                    <p className="text-[8px] truncate text-slate-600">{img.filename}</p>
                    <p className="text-[7px] text-slate-400">{img.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog logowania/rejestracji */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white text-center">
            <LogIn className="w-12 h-12 mx-auto mb-3 opacity-90" />
            <h2 className="text-xl font-bold">Zaloguj się aby zamówić</h2>
            <p className="text-sm opacity-80 mt-1">Zaloguj się lub załóż konto, aby złożyć zamówienie</p>
          </div>
          <div className="p-1">
            <AuthForm onSuccess={handleAuthSuccess} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog negocjacji */}
      <Dialog open={showNegotiation} onOpenChange={setShowNegotiation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              Negocjuj cene
            </DialogTitle>
          </DialogHeader>
          
          {negotiationOffer && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">Oferta:</p>
                <p className="font-medium">{negotiationOffer.name}</p>
                <p className="text-sm text-slate-500">Strona: {siteDetail?.site?.name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-slate-600">Aktualna cena:</span>
                  <span className="font-bold text-blue-600">
                    {negotiationOffer.price} PLN
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Proponowana cena (PLN)</Label>
                <Input
                  type="number"
                  value={negotiationData.proposed_price}
                  onChange={(e) => setNegotiationData({...negotiationData, proposed_price: e.target.value})}
                  placeholder="np. 80"
                  min="1"
                  step="1"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Wiadomosc (opcjonalnie)</Label>
                <Textarea
                  value={negotiationData.message}
                  onChange={(e) => setNegotiationData({...negotiationData, message: e.target.value})}
                  placeholder="Np. Jestem stalym klientem, zamawiam regularnie..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowNegotiation(false)}
                >
                  Anuluj
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleNegotiation}
                  disabled={negotiationLoading}
                  data-testid="send-negotiation-btn"
                >
                  {negotiationLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Wyslij propozycje"
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Twoja propozycja zostanie wyslana do administratora i wydawcy strony.
                Otrzymasz odpowiedz w wiadomosciach systemowych.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
